import { useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import {
  useCommunityAdminPosts,
  useHideDiscussion,
  useUnhideDiscussion,
  useDeleteCommunityDiscussion,
  usePinDiscussion,
  useUnpinDiscussion,
  useFreezeDiscussion,
  useUnfreezeDiscussion,
} from '../../hooks/use-admin';
import { getDiscussionPath } from '../../utils/slug';
import {
  MessageSquare,
  Search,
  Filter,
  MoreVertical,
  Eye,
  EyeOff,
  Trash2,
  Pin,
  Lock,
  Unlock,
  ExternalLink,
  Loader2,
  Calendar,
  User,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../lib/utils';

export default function CommunityAdminDiscussions() {
  const { community } = useOutletContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [actionDialog, setActionDialog] = useState({
    open: false,
    type: null,
    discussion: null,
  });
  const [reason, setReason] = useState('');

  const { data: discussionsData, isLoading } = useCommunityAdminPosts(
    community?.oid,
    {
      page,
      limit,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      search: searchQuery || undefined,
    }
  );

  const hideDiscussionMutation = useHideDiscussion(community?.oid);
  const unhideDiscussionMutation = useUnhideDiscussion(community?.oid);
  const deleteDiscussionMutation = useDeleteCommunityDiscussion(community?.oid);
  const pinDiscussionMutation = usePinDiscussion(community?.oid);
  const unpinDiscussionMutation = useUnpinDiscussion(community?.oid);
  const freezeDiscussionMutation = useFreezeDiscussion(community?.oid);
  const unfreezeDiscussionMutation = useUnfreezeDiscussion(community?.oid);

  const discussions = discussionsData?.discussions || [];
  const totalPages = Math.ceil(
    (discussionsData?.total || discussions.length) / limit
  );

  // Filter client-side if backend doesn't support it
  const filteredDiscussions = discussions.filter((discussion) => {
    const matchesSearch =
      !searchQuery ||
      discussion.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      discussion.author?.username
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase());

    let matchesStatus = statusFilter === 'all';
    if (statusFilter === 'active')
      matchesStatus = discussion.status === 'active' && !discussion.hidden;
    if (statusFilter === 'hidden') matchesStatus = discussion.hidden;
    if (statusFilter === 'pinned') matchesStatus = discussion.pinned;
    if (statusFilter === 'frozen') matchesStatus = discussion.frozen;

    return matchesSearch && matchesStatus;
  });

  const handleHide = async () => {
    await hideDiscussionMutation.mutateAsync({
      discussionOid: actionDialog.discussion.oid,
      reason,
    });
    setActionDialog({ open: false, type: null, discussion: null });
    setReason('');
  };

  const handleUnhide = async (discussionOid) => {
    await unhideDiscussionMutation.mutateAsync(discussionOid);
  };

  const handleDelete = async () => {
    await deleteDiscussionMutation.mutateAsync({
      discussionOid: actionDialog.discussion.oid,
      reason,
    });
    setActionDialog({ open: false, type: null, discussion: null });
    setReason('');
  };

  const handlePin = async (discussionOid) => {
    await pinDiscussionMutation.mutateAsync(discussionOid);
  };

  const handleUnpin = async (discussionOid) => {
    await unpinDiscussionMutation.mutateAsync(discussionOid);
  };

  const handleFreeze = async () => {
    await freezeDiscussionMutation.mutateAsync({
      discussionOid: actionDialog.discussion.oid,
      reason: reason || 'Frozen by community moderator',
    });
    setActionDialog({ open: false, type: null, discussion: null });
    setReason('');
  };

  const handleUnfreeze = async (discussionOid) => {
    await unfreezeDiscussionMutation.mutateAsync(discussionOid);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Discussions</h1>
        <p className="text-zinc-400">Manage community discussions and posts</p>
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search discussions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48 bg-zinc-800 border-zinc-700 text-white">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem
                  value="all"
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                >
                  All Discussions
                </SelectItem>
                <SelectItem
                  value="active"
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                >
                  Active
                </SelectItem>
                <SelectItem
                  value="hidden"
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                >
                  Hidden
                </SelectItem>
                <SelectItem
                  value="pinned"
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                >
                  Pinned
                </SelectItem>
                <SelectItem
                  value="frozen"
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                >
                  Frozen
                </SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={limit.toString()}
              onValueChange={(val) => {
                setLimit(parseInt(val));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-full md:w-32 bg-zinc-800 border-zinc-700 text-white">
                <SelectValue placeholder="Per page" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem
                  value="10"
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                >
                  10 per page
                </SelectItem>
                <SelectItem
                  value="20"
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                >
                  20 per page
                </SelectItem>
                <SelectItem
                  value="50"
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                >
                  50 per page
                </SelectItem>
                <SelectItem
                  value="100"
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                >
                  100 per page
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Discussions List */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            {discussionsData?.total || filteredDiscussions.length} Discussions
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Click the menu button to manage a discussion
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredDiscussions.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
              <p className="text-zinc-400">No discussions found</p>
              <p className="text-sm text-zinc-500 mt-1">
                {searchQuery || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Discussions will appear here when members post'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredDiscussions.map((discussion) => (
                <div
                  key={discussion.oid}
                  className={cn(
                    'flex items-start justify-between p-4 rounded-lg transition-colors',
                    discussion.hidden
                      ? 'bg-red-950/20 border border-red-900/30'
                      : 'bg-zinc-800/50 hover:bg-zinc-800'
                  )}
                >
                  <div className="flex-1 min-w-0">
                    {/* Title with badges */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        to={getDiscussionPath(discussion, community.slug)}
                        className="font-medium text-white hover:text-violet-400 line-clamp-1"
                      >
                        {discussion.title}
                      </Link>
                      {discussion.pinned && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
                          <Pin className="w-3 h-3 inline mr-1" />
                          Pinned
                        </span>
                      )}
                      {discussion.frozen && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">
                          <Lock className="w-3 h-3 inline mr-1" />
                          Frozen
                        </span>
                      )}
                      {discussion.hidden && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">
                          <EyeOff className="w-3 h-3 inline mr-1" />
                          Hidden
                        </span>
                      )}
                    </div>

                    {/* Meta info */}
                    <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {discussion.author?.username || 'Unknown'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(discussion.createdAt).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {discussion.views || 0} views
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />
                        {discussion.replyCount || 0} replies
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4">
                    <Link
                      to={getDiscussionPath(discussion, community.slug)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-white"
                        title="Open in new tab"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </Link>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-zinc-400 hover:text-white"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="bg-zinc-900 border-zinc-800"
                      >
                        {/* Pin/Unpin */}
                        {discussion.pinned ? (
                          <DropdownMenuItem
                            onClick={() => handleUnpin(discussion.oid)}
                            className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                          >
                            <Pin className="w-4 h-4 mr-2" />
                            Unpin Discussion
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => handlePin(discussion.oid)}
                            className="text-amber-400 hover:text-amber-300 hover:bg-zinc-800"
                          >
                            <Pin className="w-4 h-4 mr-2" />
                            Pin Discussion
                          </DropdownMenuItem>
                        )}

                        {/* Freeze/Unfreeze */}
                        {discussion.frozen ? (
                          <DropdownMenuItem
                            onClick={() => handleUnfreeze(discussion.oid)}
                            className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                          >
                            <Unlock className="w-4 h-4 mr-2" />
                            Unfreeze Discussion
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() =>
                              setActionDialog({
                                open: true,
                                type: 'freeze',
                                discussion,
                              })
                            }
                            className="text-blue-400 hover:text-blue-300 hover:bg-zinc-800"
                          >
                            <Lock className="w-4 h-4 mr-2" />
                            Freeze Discussion
                          </DropdownMenuItem>
                        )}

                        <DropdownMenuSeparator className="bg-zinc-800" />

                        {/* Hide/Unhide */}
                        {discussion.hidden ? (
                          <DropdownMenuItem
                            onClick={() => handleUnhide(discussion.oid)}
                            className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Unhide Discussion
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() =>
                              setActionDialog({
                                open: true,
                                type: 'hide',
                                discussion,
                              })
                            }
                            className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                          >
                            <EyeOff className="w-4 h-4 mr-2" />
                            Hide Discussion
                          </DropdownMenuItem>
                        )}

                        <DropdownMenuSeparator className="bg-zinc-800" />

                        {/* Delete */}
                        <DropdownMenuItem
                          onClick={() =>
                            setActionDialog({
                              open: true,
                              type: 'delete',
                              discussion,
                            })
                          }
                          className="text-red-400 hover:text-red-300 hover:bg-zinc-800"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Discussion
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-zinc-400">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hide/Delete/Freeze Dialog */}
      <Dialog
        open={actionDialog.open}
        onOpenChange={(open) =>
          !open &&
          setActionDialog({ open: false, type: null, discussion: null })
        }
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionDialog.type === 'delete' ? (
                <>
                  <Trash2 className="w-5 h-5 text-red-400" />
                  Delete Discussion
                </>
              ) : actionDialog.type === 'freeze' ? (
                <>
                  <Lock className="w-5 h-5 text-blue-400" />
                  Freeze Discussion
                </>
              ) : (
                <>
                  <EyeOff className="w-5 h-5 text-orange-400" />
                  Hide Discussion
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {actionDialog.type === 'delete' ? (
                <>
                  Are you sure you want to delete "
                  <span className="text-white font-medium">
                    {actionDialog.discussion?.title}
                  </span>
                  "? This action cannot be undone.
                </>
              ) : actionDialog.type === 'freeze' ? (
                <>
                  Freeze "
                  <span className="text-white font-medium">
                    {actionDialog.discussion?.title}
                  </span>
                  "? No new replies will be allowed.
                </>
              ) : (
                <>
                  Hide "
                  <span className="text-white font-medium">
                    {actionDialog.discussion?.title}
                  </span>
                  " from the community? Only staff will be able to see it.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-zinc-300">
                Reason{' '}
                {actionDialog.type === 'delete' ? '(required)' : '(optional)'}
              </label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Provide a reason for this action..."
                className="mt-2 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setActionDialog({ open: false, type: null, discussion: null })
              }
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              onClick={
                actionDialog.type === 'delete'
                  ? handleDelete
                  : actionDialog.type === 'freeze'
                    ? handleFreeze
                    : handleHide
              }
              disabled={
                (actionDialog.type === 'delete' && !reason) ||
                hideDiscussionMutation.isPending ||
                deleteDiscussionMutation.isPending ||
                freezeDiscussionMutation.isPending
              }
              className={
                actionDialog.type === 'delete'
                  ? 'bg-red-600 hover:bg-red-700'
                  : actionDialog.type === 'freeze'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-orange-600 hover:bg-orange-700'
              }
            >
              {(hideDiscussionMutation.isPending ||
                deleteDiscussionMutation.isPending ||
                freezeDiscussionMutation.isPending) && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
              {actionDialog.type === 'delete'
                ? 'Delete Discussion'
                : actionDialog.type === 'freeze'
                  ? 'Freeze Discussion'
                  : 'Hide Discussion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
